import type { JSX } from "preact";
import { useState } from "preact/hooks";
import { folderLabel, logout, markFolderRead, openCompose, refresh, runSearch, selectMailbox } from "../actions";
import { currentMailbox, mailboxes, showCommandPalette, showSettings, showShortcuts } from "../state";
import { prefs, setPref } from "../prefs";
import { t } from "../i18n";
import { Icon, type IconName } from "./icons";
import { Dialog } from "./kit";

interface Command {
  id: string;
  label: string;
  icon: IconName;
  hint?: string;
  group: string;
  run: () => void;
}

function buildCommands(query: string): Command[] {
  const go = t("palette.go");
  const actions = t("palette.actions");
  const view = t("palette.view");
  const cmds: Command[] = [
    { id: "compose", label: t("compose.new"), icon: "drafts", hint: "C", group: actions, run: () => openCompose() },
    { id: "refresh", label: t("list.refresh"), icon: "refresh", group: actions, run: () => void refresh() },
    {
      id: "markread",
      label: t("palette.markFolderRead", { folder: folderLabel(currentMailbox.value) }),
      icon: "mailOpen",
      group: actions,
      run: () => void markFolderRead(currentMailbox.value),
    },
    ...mailboxes.value.map<Command>((m) => ({
      id: `go:${m.path}`,
      label: t("palette.goTo", { folder: m.specialUse ? folderLabel(m.path) : m.path }),
      icon: m.specialUse === "inbox" ? "inbox" : m.specialUse === "sent" ? "sent" : m.specialUse === "drafts" ? "drafts" : m.specialUse === "trash" ? "trash" : m.specialUse === "archive" ? "archive" : m.specialUse === "junk" ? "spam" : "folder",
      group: go,
      run: () => void selectMailbox(m.path),
    })),
    {
      id: "theme",
      label: prefs.value.theme === "dark" ? t("palette.lightTheme") : t("palette.darkTheme"),
      icon: prefs.value.theme === "dark" ? "sun" : "moon",
      group: view,
      run: () => setPref("theme", prefs.value.theme === "dark" ? "light" : "dark"),
    },
    {
      id: "density",
      label: prefs.value.density === "compact" ? t("palette.comfortable") : t("palette.compact"),
      icon: "layout",
      group: view,
      run: () => setPref("density", prefs.value.density === "compact" ? "comfortable" : "compact"),
    },
    {
      id: "pane",
      label: prefs.value.readingPane === "right" ? t("palette.paneOff") : t("palette.paneRight"),
      icon: "panelLeft",
      group: view,
      run: () => setPref("readingPane", prefs.value.readingPane === "right" ? "off" : "right"),
    },
    { id: "settings", label: t("settings.title"), icon: "settings", group: view, run: () => (showSettings.value = true) },
    { id: "shortcuts", label: t("shortcuts.title"), icon: "keyboard", hint: "?", group: view, run: () => (showShortcuts.value = true) },
    { id: "logout", label: t("nav.signOut"), icon: "logout", group: actions, run: () => void logout() },
  ];

  const q = query.trim().toLowerCase();
  if (!q) return cmds;
  const words = q.split(/\s+/);
  const matched = cmds.filter((c) => words.every((w) => c.label.toLowerCase().includes(w)));
  // Free text always offers a search, here or everywhere.
  const search: Command[] = [
    { id: "search", label: t("palette.searchHere", { q: query.trim(), folder: folderLabel(currentMailbox.value) }), icon: "search", group: t("palette.search"), run: () => void runSearch(query, {}) },
    { id: "searchAll", label: t("palette.searchAll", { q: query.trim() }), icon: "globe", group: t("palette.search"), run: () => void runSearch(query, { allFolders: true }) },
  ];
  return [...matched, ...search];
}

export function CommandPalette(): JSX.Element | null {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  if (!showCommandPalette.value) return null;

  const close = (): void => {
    showCommandPalette.value = false;
    setQ("");
    setActive(0);
  };
  const cmds = buildCommands(q);
  const run = (c: Command | undefined): void => {
    if (!c) return;
    close();
    c.run();
  };

  let lastGroup = "";
  return (
    <Dialog onClose={close} label={t("palette.title")} class="max-w-lg" align="top">
      <div class="flex items-center gap-2.5 border-b border-line px-4">
        <Icon name="command" size={16} class="text-fg-3" />
        <input
          autofocus
          class="h-12 flex-1 bg-transparent text-[15px] text-fg outline-none placeholder:text-fg-4"
          placeholder={t("palette.placeholder")}
          value={q}
          onInput={(e) => {
            setQ((e.currentTarget as HTMLInputElement).value);
            setActive(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              setActive((a) => (a + (e.key === "ArrowDown" ? 1 : -1) + cmds.length) % Math.max(cmds.length, 1));
            }
            if (e.key === "Enter") {
              e.preventDefault();
              run(cmds[active]);
            }
          }}
        />
        <kbd class="kbd">Esc</kbd>
      </div>
      <ul class="max-h-[60vh] overflow-y-auto p-1.5" role="listbox">
        {cmds.map((c, i) => {
          const header = c.group !== lastGroup ? c.group : null;
          lastGroup = c.group;
          return (
            <li key={c.id}>
              {header && <div class="px-2.5 pb-1 pt-2 text-xs font-medium text-fg-3">{header}</div>}
              <button
                class="menu-item"
                role="option"
                aria-selected={i === active}
                data-active={i === active ? "true" : undefined}
                onMouseEnter={() => setActive(i)}
                onClick={() => run(c)}
              >
                <Icon name={c.icon} size={16} />
                <span class="flex-1 truncate">{c.label}</span>
                {c.hint && <kbd class="kbd">{c.hint}</kbd>}
              </button>
            </li>
          );
        })}
      </ul>
    </Dialog>
  );
}
